import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { purchaseRequestsApi, approvalsApi, purchaseOrdersApi } from '../lib/api';
import {
  PR_STATUS_LABELS, PR_STATUS_COLORS, PR_TYPE_LABELS,
  ROLE_LABELS, UserRole,
} from '../types';
import type { PurchaseRequest, ApprovalStep, PurchaseOrder } from '../types';
import AppShell from '../components/AppShell';

const SANS = "'Roboto', system-ui, sans-serif";

export default function PurchaseRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [pr, setPr] = useState<PurchaseRequest | null>(null);
  const [steps, setSteps] = useState<ApprovalStep[]>([]);
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [deciding, setDeciding] = useState<string | null>(null);
  const [comments, setComments] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const load = () => {
    if (!id || !user) return;
    Promise.all([
      purchaseRequestsApi.get(id),
      approvalsApi.stepsForPr(id),
      purchaseOrdersApi.getByPr(id),
    ])
      .then(([prData, stepsData, poData]) => {
        setPr(prData);
        setSteps(stepsData);
        setPo(poData);
      })
      .catch((e) => setError(e.message))
      .finally(() => setFetching(false));
  };

  useEffect(() => { if (user) load(); }, [user, id]);

  if (loading || !user) return null;
  if (fetching) return <AppShell user={user} title="Purchase Request"><p style={{ color: '#94a3b8' }}>Loading…</p></AppShell>;
  if (error || !pr) return <AppShell user={user} title="Purchase Request"><ErrorBanner message={error ?? 'Not found'} /></AppShell>;

  const amount = parseFloat(pr.amount).toLocaleString('en-US', { style: 'currency', currency: pr.currency });
  const canSubmit = pr.status === 'draft' && pr.requesterId === user.id;
  const canCancel = ['draft', 'submitted', 'pending_approval'].includes(pr.status) && pr.requesterId === user.id;

  const myPendingStep = steps.find(
    (s) => s.status === 'pending' &&
      (s.approverId === user.id ||
        (!s.approverId && s.approverRole === user.role)),
  );

  const handleSubmit = async () => {
    try {
      await purchaseRequestsApi.submit(pr.id);
      load();
    } catch (e: any) { setActionError(e.message); }
  };

  const handleCancel = async () => {
    if (!confirm('Cancel this request?')) return;
    try {
      await purchaseRequestsApi.cancel(pr.id);
      navigate('/purchase-requests');
    } catch (e: any) { setActionError(e.message); }
  };

  const handleDecide = async (decision: 'approve' | 'reject') => {
    if (!myPendingStep) return;
    if (decision === 'reject' && !comments.trim()) {
      setActionError('Rejection reason is required');
      return;
    }
    setDeciding(myPendingStep.id);
    setActionError(null);
    try {
      await approvalsApi.decide(myPendingStep.id, decision, comments || undefined);
      setComments('');
      load();
    } catch (e: any) { setActionError(e.message); }
    finally { setDeciding(null); }
  };

  return (
    <AppShell user={user} title={pr.title}>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to="/purchase-requests" style={{ fontSize: '0.8rem', color: '#94a3b8', textDecoration: 'none' }}>
          ← Purchase Requests
        </Link>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 300, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
            {pr.title}
          </h1>
          <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginTop: '0.3rem' }}>
            {PR_TYPE_LABELS[pr.type]} · Raised by {pr.requester?.name ?? 'Unknown'} · {new Date(pr.createdAt).toLocaleDateString()}
          </p>
        </div>
        <StatusBadge status={pr.status} />
      </div>

      {actionError && <ErrorBanner message={actionError} />}

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.5rem' }}>
        {/* Left: details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <Card title="Request Details">
            <DetailRow label="Amount" value={amount} accent />
            <DetailRow label="Vendor" value={pr.vendor?.name ?? (pr.metadata?.vendorName as string) ?? '—'} />
            <DetailRow label="Department" value={pr.department?.name ?? '—'} />
            <DetailRow label="Category" value={pr.spendCategory ?? '—'} />
            {pr.seats && <DetailRow label="Seats" value={String(pr.seats)} />}
            {pr.periodStart && <DetailRow label="Period" value={`${pr.periodStart} → ${pr.periodEnd ?? '?'}`} />}
          </Card>

          {pr.description && (
            <Card title="Description">
              <p style={{ fontSize: '0.88rem', color: '#475569', margin: 0, lineHeight: 1.6 }}>{pr.description}</p>
            </Card>
          )}

          {pr.businessCase && (
            <Card title="Business Case">
              <p style={{ fontSize: '0.88rem', color: '#475569', margin: 0, lineHeight: 1.6 }}>{pr.businessCase}</p>
            </Card>
          )}

          {(canSubmit || canCancel) && (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {canSubmit && (
                <button onClick={handleSubmit} style={primaryBtn}>Submit for Approval</button>
              )}
              {canCancel && (
                <button onClick={handleCancel} style={dangerBtn}>Cancel Request</button>
              )}
            </div>
          )}
        </div>

        {/* Right: approval chain + PO */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <Card title="Approval Chain">
            {steps.length === 0 ? (
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: 0 }}>
                {pr.status === 'draft' ? 'Submit to start approval.' : 'No approval steps.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {steps.map((step) => <StepRow key={step.id} step={step} />)}
              </div>
            )}
          </Card>

          {myPendingStep && (
            <Card title="Your Decision">
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Comments (required for rejection)"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', marginBottom: '0.75rem' }}
              />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => handleDecide('approve')}
                  disabled={!!deciding}
                  style={approveBtn}
                >
                  {deciding === myPendingStep.id ? '…' : 'Approve'}
                </button>
                <button
                  onClick={() => handleDecide('reject')}
                  disabled={!!deciding}
                  style={rejectBtn}
                >
                  Reject
                </button>
              </div>
            </Card>
          )}

          {po && (
            <Card title="Purchase Order">
              <DetailRow label="PO Number" value={po.poNumber} accent />
              <DetailRow label="Amount" value={parseFloat(po.amount).toLocaleString('en-US', { style: 'currency', currency: po.currency })} />
              <DetailRow label="Status" value={po.status.toUpperCase()} />
            </Card>
          )}

          {pr.status === 'rejected' && pr.rejectionReason && (
            <div style={{
              backgroundColor: '#fee2e2', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 10, padding: '1rem',
            }}>
              <p style={{ fontSize: '0.75rem', fontWeight: 500, color: '#94a3b8', margin: '0 0 0.3rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Rejection reason
              </p>
              <p style={{ fontSize: '0.88rem', color: '#991b1b', margin: 0 }}>{pr.rejectionReason}</p>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function StepRow({ step }: { step: ApprovalStep }) {
  const statusColor: Record<string, string> = {
    pending: '#f59e0b', approved: '#10b981', rejected: '#ef4444',
    skipped: '#94a3b8', escalated: '#8b5cf6',
  };
  const color = statusColor[step.status] ?? '#94a3b8';

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <p style={{ fontSize: '0.85rem', color: '#0f172a', margin: 0, fontWeight: 400 }}>
          Step {step.stepOrder}: {step.approver?.name ?? ROLE_LABELS[step.approverRole as UserRole] ?? step.approverRole ?? 'TBD'}
        </p>
        {step.comments && (
          <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '0.2rem 0 0' }}>{step.comments}</p>
        )}
      </div>
      <span style={{ fontSize: '0.72rem', fontWeight: 400, color, border: `1px solid ${color}`, borderRadius: 12, padding: '0.15rem 0.5rem', whiteSpace: 'nowrap' }}>
        {step.status}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: PurchaseRequest['status'] }) {
  const color = PR_STATUS_COLORS[status];
  return (
    <span style={{ fontSize: '0.8rem', fontWeight: 400, color, border: `1px solid ${color}`, borderRadius: 20, padding: '0.25rem 0.75rem', whiteSpace: 'nowrap' }}>
      {PR_STATUS_LABELS[status]}
    </span>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10,
      padding: '1.25rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      <p style={{ fontSize: '0.7rem', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 1rem' }}>{title}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>{children}</div>
    </div>
  );
}

function DetailRow({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{label}</span>
      <span style={{ fontSize: '0.88rem', color: accent ? '#10b981' : '#0f172a', fontWeight: accent ? 400 : 300 }}>{value}</span>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      backgroundColor: '#fee2e2', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8, padding: '0.75rem 1rem', color: '#991b1b',
      fontSize: '0.85rem', marginBottom: '1rem',
    }}>
      {message}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 8, padding: '0.6rem 0.85rem', color: '#0f172a', fontSize: '0.88rem',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', fontWeight: 300,
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
const primaryBtn: React.CSSProperties = {
  backgroundColor: '#0ea5e9', color: '#ffffff', border: 'none', borderRadius: 8,
  padding: '0.6rem 1.25rem', fontSize: '0.85rem', fontWeight: 400, cursor: 'pointer',
  fontFamily: SANS,
};
const dangerBtn: React.CSSProperties = {
  backgroundColor: 'transparent', color: '#ef4444',
  border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8,
  padding: '0.6rem 1.25rem', fontSize: '0.85rem', cursor: 'pointer', fontFamily: SANS,
};
const approveBtn: React.CSSProperties = {
  backgroundColor: '#10b981', color: '#ffffff', border: 'none', borderRadius: 8,
  padding: '0.6rem 1.25rem', fontSize: '0.85rem', fontWeight: 400, cursor: 'pointer',
  flex: 1, fontFamily: SANS,
};
const rejectBtn: React.CSSProperties = {
  backgroundColor: 'transparent', color: '#ef4444',
  border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8,
  padding: '0.6rem 1.25rem', fontSize: '0.85rem', cursor: 'pointer',
  flex: 1, fontFamily: SANS,
};
